/**
 * Closing tab → Readiness: the full per-loan readiness object
 * (`GET /closing/readiness/{dealId}`, closing-readiness.md §3) plus the
 * schedule editor (§4) and the stored AI readiness review (§6).
 *
 * The body is the SAME panel the calendar detail drawer renders
 * (ClosingPanelSections.js): status strip → blocker callout → Readiness
 * blocks → the quiet sections (closing logistics, people & contacts, wire
 * instructions). §3 names one object for "the sidebar / the Closing tab" —
 * the tab only drops the drawer's identity crumb and "Open loan file", which
 * are redundant on the loan itself. Pipeline status sits at the top; the
 * level is secondary to it. On an acquisition the same panel reads as a trade
 * settlement (`mode = "trade"`).
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import CloseRounded from "@mui/icons-material/CloseRounded";
import EditCalendarOutlined from "@mui/icons-material/EditCalendarOutlined";
import FactCheckOutlined from "@mui/icons-material/FactCheckOutlined";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import { Box, Button, CircularProgress, IconButton, Tooltip } from "@mui/material";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import PrimaryContentCard from "../../../../_src_shared/components/layout/PrimaryContentCard";
import { closingReadinessApi } from "../../../../api/closingReadinessApi";
import { losAiReviewsApi } from "../../../../api/losAiReviewsApi";
import {
  Callout,
  ClosingLogisticsSection,
  FundedLine,
  PanelDivider,
  PeopleContactsSection,
  SectionHead,
  StatusStrip,
  WireInstructionsSection,
} from "../../../../components/closingReadiness/ClosingPanelSections";
import ReadinessDetail, {
  CollapseAllContext,
  SECTION_ID,
} from "../../../../components/closingReadiness/ReadinessDetail";
import ScheduleEditForm from "../../../../components/closingReadiness/ScheduleEditForm";
import {
  CollapseAllButton,
  Muted,
  useCollapseAllState,
} from "../../../../components/closingReadiness/ReadinessBits";
import {
  C,
  cardDate,
  eventNoun,
  readinessToCard,
  scheduleFieldHref,
} from "../../../../components/closingReadiness/closingReadinessUtils";
import { TYPE } from "../../../../components/closingReadiness/typography";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import ConfirmationDialog from "../../../../components/ui/ConfirmationDialog";
import { usePermissionsHook } from "../../../../hooks/usePermissionsHook";
import { useUnderwritingHook } from "../../../../hooks/useUnderwritingHook";
import { useIsAcquisitions } from "../../../../pipelines/PipelineContext";
import { filePreviewSetData } from "../../../../redux/reducers/filePreviewReducer";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";

const LoanClosingSubTabReadiness = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const isAcquisitions = useIsAcquisitions();
  const { isAdmin, isCloser, isCapitalMarkets } = usePermissionsHook();
  const { getLoanById } = useUnderwritingHook();
  const dealId = loanDrilldown?._id;
  const pipeline = isAcquisitions ? "acquisition" : "preclose";

  const [readiness, setReadiness] = useState(null);
  const [aiFull, setAiFull] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [running, setRunning] = useState(false);
  const [confirmAi, setConfirmAi] = useState(false);
  const [collapseAll, setCollapseAll, focusSection] = useCollapseAllState();

  const load = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    try {
      const r = await closingReadinessApi.getReadiness(dealId, { pipeline });
      setReadiness(r);
      if (r?.aiReview) {
        closingReadinessApi
          .getAiReview(dealId, { pipeline })
          .then(setAiFull)
          .catch(() => setAiFull(null));
      } else {
        setAiFull(null);
      }
    } catch (err) {
      console.error("readiness fetch error:", err);
      setReadiness(null);
    } finally {
      setLoading(false);
    }
  }, [dealId, pipeline]);

  useEffect(() => {
    load();
  }, [load]);

  // The full object keeps the scheduling / activity signals in nested blocks;
  // the panel pieces read the CARD shape, so lift them the way the drawer does.
  const view = useMemo(() => readinessToCard(readiness), [readiness]);
  const date = useMemo(() => cardDate(view), [view]);
  const trade = isAcquisitions;
  const closed = !!view?.closed;

  // The readiness rows only carry a summary of each file; the previewer needs
  // the full loan-document object from the loan.
  const docsById = useMemo(() => {
    const m = new Map();
    (loanDrilldown?.loanDocuments || []).forEach((d) => {
      if (d?._id) m.set(String(d._id), d);
    });
    return m;
  }, [loanDrilldown?.loanDocuments]);
  const resolveDoc = useCallback((id) => (id ? docsById.get(String(id)) || null : null), [docsById]);
  const onPreviewDoc = useCallback((doc) => dispatch(filePreviewSetData(doc)), [dispatch]);

  // Deep links: open ONE readiness sub-section and scroll it into view ("Go to
  // scheduling entries" in the callout, "detail in Documents ↑" in logistics).
  const jumpTo = useCallback(
    (id) => {
      focusSection(id);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
    },
    [focusSection],
  );
  const jumpToDocuments = useCallback(() => jumpTo(SECTION_ID.documents), [jumpTo]);
  const jumpToScheduling = useCallback(() => jumpTo(SECTION_ID.scheduling), [jumpTo]);

  // After a schedule write the response carries the fresh readiness; the loan
  // itself changed too (dates), so refetch it into Redux.
  const onScheduleSaved = useCallback(
    (res) => {
      setEditing(false);
      if (res?.readiness) setReadiness(res.readiness);
      else load();
      getLoanById({ loanId: dealId, onSuccessFn: (data) => dispatch(loanDrilldownSet(data)) });
    },
    [dealId, dispatch, getLoanById, load],
  );

  // After a missing document slot is filled: refetch the loan into Redux (the
  // new file has to resolve for preview) and recompute readiness.
  const onDocsChanged = useCallback(() => {
    if (!dealId) return;
    getLoanById({ loanId: dealId, onSuccessFn: (data) => dispatch(loanDrilldownSet(data)) });
    load();
  }, [dealId, dispatch, getLoanById, load]);

  const runAi = async () => {
    if (!dealId) return;
    setConfirmAi(false);
    setRunning(true);
    try {
      const res = await losAiReviewsApi.runClosingReadiness(dealId, { force: true });
      enqueueSnackbar(
        res?.status === "skipped"
          ? res?.message || "Review skipped."
          : "Closing readiness review queued — you'll be notified when it finishes.",
        { variant: res?.status === "skipped" ? "info" : "success" },
      );
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.detail || "Could not queue the review.", { variant: "error" });
    } finally {
      setRunning(false);
    }
  };

  const canEdit = isAdmin || isCloser || isCapitalMarkets;
  const noun = eventNoun(readiness || { pipeline });

  // Scheduling entries rows: edit one in place, or jump to where the loan
  // file edits it (same gate as "Adjust dates").
  const scheduleEdit = useMemo(
    () => ({
      canEdit: canEdit && !closed,
      onSaved: onScheduleSaved,
      onGoTo: (field) => {
        const href = scheduleFieldHref({ dealId, readiness, pathname: location.pathname, field });
        if (href) navigate(href);
      },
    }),
    [canEdit, closed, onScheduleSaved, dealId, readiness, location.pathname, navigate],
  );

  return (
    <PrimaryContentCard
      maxWidth={false}
      title={`${noun} readiness`}
      subTitle={
        <Muted>
          One deterministic signal — closers read it as clear-to-close, Capital Markets as ready-to-fund.
        </Muted>
      }
      endContent={
        <Flx ac g={0.75}>
          <Tooltip title="Recompute" arrow>
            <span>
              <IconButton size="small" onClick={load} disabled={loading}>
                <RefreshRounded sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          {isAdmin && (
            <Tooltip
              arrow
              title={
                <Box sx={{ fontSize: 12, lineHeight: 1.45 }}>
                  Queues the AI closing-readiness review for this loan (re-runs even if one
                  already exists). It reads the deterministic signal, the open conditions,
                  exceptions and documents, then writes a headline, a readiness call, the
                  gating items with owners and next actions, and risks. You'll get a
                  notification when it finishes; the AI section below refreshes on
                  recompute. The AI call is never more optimistic than the deterministic
                  level.
                </Box>
              }
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  color="gray"
                  startIcon={running ? <CircularProgress size={12} /> : <AutoAwesomeOutlined />}
                  onClick={() => setConfirmAi(true)}
                  disabled={running}
                  sx={{ textTransform: "none", borderRadius: "8px" }}
                >
                  AI readiness call
                </Button>
              </span>
            </Tooltip>
          )}
          {canEdit && !closed && (
            <Button
              size="small"
              variant={editing ? "outlined" : "contained"}
              color={editing ? "gray" : "primary"}
              startIcon={editing ? <CloseRounded /> : <EditCalendarOutlined />}
              onClick={() => setEditing((e) => !e)}
              disabled={!readiness}
              sx={{ textTransform: "none", borderRadius: "8px" }}
            >
              {editing ? "Cancel" : "Adjust dates"}
            </Button>
          )}
        </Flx>
      }
      headerSx={{ pb: 1 }}
    >
      {loading && !readiness ? (
        <Flx center sx={{ py: 6 }}>
          <CircularProgress size={26} />
        </Flx>
      ) : !readiness ? (
        <Muted>Readiness isn't available for this loan.</Muted>
      ) : (
        <Box sx={{ pt: 1 }}>
          {/* One status strip, stated once — the same one the calendar drawer
              opens with. */}
          <StatusStrip view={view} trade={trade} date={date} closed={closed} sx={{ mt: 0 }} />

          {editing && (
            <Box sx={{ mt: 1.75, border: `1px solid ${C.line}`, borderRadius: "10px", px: 2.5, py: 2.25 }}>
              <Txt sx={{ ...TYPE.sectionH, fontSize: 13, display: "block", mb: 2.5 }}>
                Adjust {noun.toLowerCase()} dates
              </Txt>
              <ScheduleEditForm readiness={readiness} onSaved={onScheduleSaved} onCancel={() => setEditing(false)} />
            </Box>
          )}

          {/* One callout: what stands between this loan and a schedule, or the
              funded confirmation once it has closed. */}
          {closed ? (
            <FundedLine trade={trade} date={date} />
          ) : (
            <Callout view={view} readiness={readiness} onJumpToScheduling={jumpToScheduling} />
          )}

          <PanelDivider />

          {/* Readiness — the tab's reason for existing. */}
          <SectionHead
            icon={FactCheckOutlined}
            title="Readiness"
            right={<CollapseAllButton allOpen={collapseAll.open} onSet={setCollapseAll} />}
          />
          <Box sx={{ mt: "9px" }}>
            <CollapseAllContext.Provider value={collapseAll}>
              <ReadinessDetail
                readiness={readiness}
                resolveDoc={resolveDoc}
                onPreviewDoc={onPreviewDoc}
                aiReviewFull={aiFull}
                loan={loanDrilldown}
                onDocsChanged={onDocsChanged}
                hideHeader
                scheduleEdit={scheduleEdit}
              />
            </CollapseAllContext.Provider>
          </Box>

          <PanelDivider />

          {/* The quiet sections: flat rows that fold open one at a time. The
              loan is already in Redux here, so contacts + wire read from it. */}
          <Box>
            <ClosingLogisticsSection view={view} trade={trade} onJumpToDocuments={jumpToDocuments} />
            {/* People & contacts + wire — preclose only (they live on the loan). */}
            {!trade && (
              <>
                <PeopleContactsSection full={loanDrilldown} view={view} />
                <WireInstructionsSection full={loanDrilldown} view={view} />
              </>
            )}
          </Box>
        </Box>
      )}
      <ConfirmationDialog
        open={confirmAi}
        title="Run AI readiness call?"
        message="Runs an AI review of this loan's closing readiness. You'll be notified when it's done."
        confirmText="Run"
        onConfirm={runAi}
        onCancel={() => setConfirmAi(false)}
      />
    </PrimaryContentCard>
  );
};

export default LoanClosingSubTabReadiness;
